/**
 * CL-ACCESS-FINANCIALS-001B — Explicit vs legacy section access resolution (pure).
 */

import { normalizeCollaboratorPermissions } from "./projectCollaboratorPermissions";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import {
  SECTION_ACCESS_PERMISSION_KEYS,
  isCustomAccessScope,
  readExplicitBoolean,
  readExplicitFalse,
  readPermissionTriState,
} from "./projectStaffFinancialPermissionsModel";

function safeStr(value) {
  return String(value ?? "").trim().toLowerCase();
}

function legacyNormalizedPermissions(rawPermissions = {}, context = {}) {
  return normalizeCollaboratorPermissions(rawPermissions, context);
}

function inferLegacySectionAccess(sectionKey, accessScope, legacyPermissions = {}) {
  const scope = safeStr(accessScope) || "full_project";
  const p = legacyPermissions;

  if (sectionKey === "canAccessOverview") {
    return scope === "full_project" || scope === "shared_files" || scope === "concept_only" || scope === "permit_drawings";
  }
  if (sectionKey === "canAccessClientDocs") {
    return Boolean(p.canUploadFiles) || Boolean(p.canViewPhotos);
  }
  if (sectionKey === "canAccessMasterFiles") {
    return scope === "full_project";
  }
  if (sectionKey === "canAccessPlanning") {
    return Boolean(p.canViewPlanning);
  }
  if (sectionKey === "canAccessReview") {
    return Boolean(p.canReviewDrawings);
  }
  if (sectionKey === "canAccessPermits") {
    return scope === "permit_drawings" || scope === "full_project";
  }
  if (sectionKey === "canAccessConstruction") {
    return false;
  }
  if (sectionKey === "canAccessTrades") {
    return false;
  }
  if (sectionKey === "canAccessFinancials") {
    return Boolean(p.canManageInvoices);
  }
  if (sectionKey === "canAccessTimeLog") {
    return Boolean(p.canLogTime) || Boolean(p.canViewTimeLog);
  }
  return false;
}

/**
 * Resolve one section gate. Explicit false always denies. Missing on custom denies.
 * Missing on legacy scopes may infer from existing collaborator permissions.
 */
export function resolveSectionAccessPermission(
  sectionKey,
  {
    rawPermissions = {},
    accessScope = "full_project",
    role = "collaborator",
    accountingPermissionsUiEnabled = null,
  } = {}
) {
  const tri = readPermissionTriState(rawPermissions, sectionKey);
  if (tri === "true") return true;
  if (tri === "false") return false;

  const gateOn = projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled);
  if (!gateOn) {
    return inferLegacySectionAccess(
      sectionKey,
      accessScope,
      legacyNormalizedPermissions(rawPermissions, { role, accessScope })
    );
  }

  if (isCustomAccessScope(accessScope)) {
    return false;
  }

  return inferLegacySectionAccess(
    sectionKey,
    accessScope,
    legacyNormalizedPermissions(rawPermissions, { role, accessScope })
  );
}

export function resolveSectionAccessMap({
  rawPermissions = {},
  accessScope = "full_project",
  role = "collaborator",
  accountingPermissionsUiEnabled = null,
} = {}) {
  return SECTION_ACCESS_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = resolveSectionAccessPermission(key, {
      rawPermissions,
      accessScope,
      role,
      accountingPermissionsUiEnabled,
    });
    return acc;
  }, {});
}

export function customScopeDeniesImplicitFileAccess({
  accessScope = "",
  accountingPermissionsUiEnabled = null,
} = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return false;
  }
  return isCustomAccessScope(accessScope);
}

export function assignmentShowsProjectIdentityHeaderOnly({
  rawPermissions = {},
  accessScope = "full_project",
  role = "collaborator",
  accountingPermissionsUiEnabled = null,
} = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return false;
  }
  const overview = resolveSectionAccessPermission("canAccessOverview", {
    rawPermissions,
    accessScope,
    role,
    accountingPermissionsUiEnabled,
  });
  return isCustomAccessScope(accessScope) && !overview;
}

export function resolveRawPermissionValue(rawPermissions = {}, key = "") {
  return {
    triState: readPermissionTriState(rawPermissions, key),
    explicitTrue: readExplicitBoolean(rawPermissions, key),
    explicitFalse: readExplicitFalse(rawPermissions, key),
  };
}
